<template>
  <div class="md-layout">
    <CSLoading :is-loading="isLoading"></CSLoading>
    <div class="md-layout-item md-size-100 product-list">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <span class="material-symbols-outlined">package_2</span>
          </div>
          <h4 class="title">{{ $t("products.products") }}</h4>
          <md-button v-if="hasPermission('Write', 'Products', 'Export')" class="md-success md-sm export-button" @click="exportProducts">{{ $t("products.export") }}</md-button>
        </md-card-header>
        <md-card-content>
          <div class="table-sticky">
            <v-server-table :columns="tableColumns" :options="tableOptions" ref="table" class="table-products" id="table-products">
              <template #h__id>
                <SortMenu :label="$t('products.table.identity')" :options="identitySortOptions" :order="currentOrder" @sort="sortBy" />
              </template>
              <template #h__shippingStats>
                <SortMenu :label="$t('products.shippingStats')" :options="shippingStatsSortOptions" :order="currentOrder" @sort="sortBy" />
              </template>
              <template #h__kpi>
                <SortMenu :label="$t('products.table.kpi')" :options="kpiSortOptions" :order="currentOrder" @sort="sortBy" />
              </template>
              <template #h__hs>
                <div class="head-hs">{{ $t("products.hsCode") }}</div>
              </template>
              <template #actions="{ row }">
                <div class="md-table-cell-container cell-actions">
                  <md-menu v-if="hasPermission('Write', 'Products', 'Edit') || hasPermission('Write', 'Products', 'BulkGood')" md-size="big" md-direction="bottom-start">
                    <md-button :disabled="selectedProduct" class="md-icon-button" md-menu-trigger>
                      <md-icon>edit</md-icon>
                    </md-button>
                    <md-menu-content>
                      <md-menu-item v-if="hasPermission('Write', 'Products', 'Edit')" @click="showEditModal(row)">
                        <span>{{ $t("products.editProduct") }}</span>
                      </md-menu-item>

                      <md-menu-item v-if="hasPermission('Write', 'Products', 'BulkGood')" @click="handleCreateBulkGood(row)">
                        <span>{{ $t("products.createBGoods") }}</span>
                      </md-menu-item>
                    </md-menu-content>
                  </md-menu>
                </div>
              </template>
              <template #image="{ row }">
                <ProductImage :sku="row.sku" :size="56" @zoom="zoomUrl = $event" />
              </template>
              <template #id="{ row }">
                <div class="cell-identity">
                  <div class="cell-idline">#{{ row.id }}</div>
                  <div class="cell-sku">{{ row.sku }}</div>
                  <div class="cell-idline">{{ row.gtin || "–" }}</div>
                  <div class="cell-name">{{ row.name }}</div>
                </div>
              </template>
              <template #stock="{ row }">
                <div class="cell-stock">
                  <div class="text-nowrap">
                    {{ $t("products.table.inStock") }}:
                    <inventory-detail-tooltip
                      :rows="stockRows(row)"
                      :value="row.inStock || 0"
                      :is-loading="!!stockLoading[row.sku]"
                      :can-open="(row.inStock || 0) > 0"
                      @open="loadStock(row.sku)"
                    >
                      <b>{{ row.inStock || 0 }}</b>
                    </inventory-detail-tooltip>
                  </div>
                  <div class="text-nowrap">
                    {{ $t("products.table.inFlow") }}:
                    <hover-popover class="inflow-value" :class="{ 'inflow-value--active': row.inFlow }" :disabled="!row.inFlow" popover-class="inflow-tooltip" @show="loadInflow(row)">
                      {{ row.inFlow || "–" }}
                      <template #content>
                        <div v-if="!inflows[row.id]">{{ $t("products.inflow.loading") }}</div>
                        <div v-else-if="!inflows[row.id].length">{{ $t("products.inflow.none") }}</div>
                        <table v-else class="inflow-table">
                          <thead>
                            <tr>
                              <th>{{ $t("products.inflow.container") }}</th>
                              <th>{{ $t("products.inflow.arrival") }}</th>
                              <th>{{ $t("products.inflow.quantity") }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="i in inflows[row.id]" :key="i.shipmentRecordId">
                              <td :title="i.shipmentRecordName">{{ i.containerId || "–" }}</td>
                              <td>{{ i.arrivalDate ? formatDate(i.arrivalDate) : "–" }}</td>
                              <td class="text-right">{{ i.quantity }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </template>
                    </hover-popover>
                  </div>
                </div>
              </template>
              <template #shippingStats="{ row }">
                <div class="cell-outboundCarrier">
                  <div class="text-nowrap">1d: {{ row.productsShippingLastDay || 0 }} {{ $t("products.shippingStatsPcs") }}</div>
                  <div class="text-nowrap">7d: {{ row.productsShipping7Days || 0 }} {{ $t("products.shippingStatsPcs") }}</div>
                  <div class="text-nowrap">30d: {{ row.productsShipping30Days || 0 }} {{ $t("products.shippingStatsPcs") }}</div>
                  <div class="text-nowrap">90d: {{ row.productsShipping90Days || 0 }} {{ $t("products.shippingStatsPcs") }}</div>
                  <div class="text-nowrap">365d: {{ row.productsShipping365Days || 0 }} {{ $t("products.shippingStatsPcs") }}</div>
                </div>
              </template>
              <template #shippingSize="{ row }">
                <div class="cell-outboundCarrier">
                  <div class="text-nowrap">{{ dimensions(row) }}</div>
                  <div class="text-nowrap">{{ row.shippingSizeWeight | formatNumberGerman(2) }} kg</div>
                  <div v-if="row.bulky === 'true'" class="text-nowrap bulky-mark"><span class="material-symbols-outlined">warning</span> {{ $t("products.bulky") }}</div>
                </div>
              </template>
              <template #hs="{ row }">
                <div class="cell-hs" v-if="row.condition === 'B-Ware'">&ndash;</div>
                <div class="cell-hs" v-else>
                  <div class="text-nowrap">UK: {{ row.hsUK || 0 }}</div>
                  <div class="text-nowrap">EU: {{ row.hsEU || 0 }}</div>
                  <div class="text-nowrap">CH: {{ row.hsCH || 0 }}</div>
                  <div class="text-nowrap">{{ $t("products.customsTariff") }}: {{ (row.customsTariff || 0) | formatUnit("%", 1, 1) }}</div>
                </div>
              </template>
              <template #outboundCarrier="{ row }">
                <div class="cell-carrier">
                  <div class="text-nowrap" v-for="c in carrierLines(row)" :key="c.region">
                    <span class="carrier-region">{{ c.region }}:</span>
                    <img v-if="carrierLogo(c.name)" :src="carrierLogo(c.name)" :alt="c.name" :title="c.name" class="carrier-icon" />
                    <span v-else>{{ c.name || "–" }}</span>
                    <span v-if="c.cost != null" class="carrier-cost">{{ c.cost | formatNumberGerman(2) }} €</span>
                  </div>
                </div>
              </template>
              <template #kpi="{ row }">
                <div v-if="row.kpi" class="cell-kpi">
                  <div v-for="line in kpiLines(row.kpi)" :key="line.key" class="kpi-line">
                    <span class="kpi-label" :title="line.title">{{ line.label }}</span>
                    <span class="kpi-value">{{ line.value }}</span>
                  </div>
                </div>
                <div v-else class="cell-idline">{{ $t("products.kpi.pending") }}</div>
              </template>
              <template #supplier="{ row }">
                <div class="cell-supplier">
                  <div v-for="(s, idx) in row.suppliers || []" :key="s.id" :class="{ 'supplier-primary': idx === 0 && row.suppliers.length > 1 }">{{ s.name }}</div>
                  <div class="text-nowrap">MOQ: {{ row.moq != null ? row.moq : "–" }}</div>
                  <div class="text-nowrap eol-indicator">
                    EOL:
                    <md-icon v-if="row.eol" class="eol-yes">check</md-icon>
                    <md-icon v-else class="eol-no">close</md-icon>
                  </div>
                  <div class="text-nowrap">{{ $t("products.table.cab") }}: {{ row.cabQuantity | formatUnit("pcs") }}</div>
                  <div class="text-nowrap">{{ row.condition === "B-Ware" ? $t("products.conditionBWare") : $t("products.conditionNew") }}</div>
                </div>
              </template>
              <template #masterboxDementions="{ row }">
                <div class="cell-outboundCarrier">
                  <div class="text-nowrap">{{ $t("products.table.skuMasterbox") }}: {{ row.skuMasterbox }}</div>
                  <div class="text-nowrap">{{ $t("products.table.mbSkuAmount") }}: {{ row.mbSkuAmount | formatUnit("pcs") }}</div>
                  <div class="text-nowrap">{{ $t("products.table.mbSkuLength") }}: {{ row.mbSkuLength | formatUnit("cm") }}</div>
                  <div class="text-nowrap">{{ $t("products.table.mbSkuWidth") }}: {{ row.mbSkuWidth | formatUnit("cm") }}</div>
                  <div class="text-nowrap">{{ $t("products.table.mbSkuHeight") }}: {{ row.mbSkuHeight | formatUnit("cm") }}</div>
                  <div class="text-nowrap">{{ $t("products.table.mbSkuWeight") }}: {{ row.mbSkuWeight | formatUnit("kg") }}</div>
                </div>
              </template>
              <template #shippingWeights="{ row }">
                <div class="cell-outboundCarrier">
                  <div class="text-nowrap">{{ $t("products.table.skuPaperWeight") }}: {{ row.skuPaperWeight | formatUnit("gr") }}</div>
                  <div class="text-nowrap">{{ $t("products.table.skuPlasticWeight") }}: {{ row.skuPlasticWeight | formatUnit("gr") }}</div>
                  <div class="text-nowrap">{{ $t("products.table.skuWoodWeight") }}: {{ row.skuWoodWeight | formatUnit("gr") }}</div>
                  <div class="text-nowrap">{{ $t("products.table.skuMetalWeight") }}: {{ row.skuMetalWeight | formatUnit("gr") }}</div>
                </div>
              </template>
            </v-server-table>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <div class="pd-dock">
      <a v-if="hasPermission('Write', 'Products', 'Create')" class="pd-dock-trigger" :title="$t('products.createProduct')" @click="showCreateModal()">
        <md-icon>add</md-icon>
      </a>
      <div class="pd-dock-search">
        <a class="pd-dock-trigger" :class="{ 'pd-dock-trigger--active': filters.keywords || filters.sku }" :title="$t('products.search.title')" @click="dockSearchOpen = !dockSearchOpen">
          <md-icon>search</md-icon>
        </a>
        <div v-show="dockSearchOpen" class="pd-dock-search-panel">
          <md-field md-clearable>
            <label>{{ $t("products.keywords") }}</label>
            <md-input v-model="filters.keywords" @input="debounceApplyFilters"></md-input>
          </md-field>
          <md-field md-clearable>
            <label>{{ $t("products.sku") }}</label>
            <md-input v-model="filters.sku" @input="debounceApplyFilters"></md-input>
          </md-field>
          <md-switch v-model="filters.exactMatchSku" @change="debounceApplyFilters">{{ $t("products.exactMatch") }}</md-switch>
        </div>
      </div>
      <ProductFilterDrawer @apply-filters="onDrawerFilters" />
    </div>
    <div class="pd-dock-info" :class="{ 'pd-dock-info--collapsed': infoCollapsed }">
      <a class="pd-dock-info-toggle" :title="$t(infoCollapsed ? 'procurement.summary.expand' : 'procurement.summary.collapse')" @click="infoCollapsed = !infoCollapsed">
        <md-icon>{{ infoCollapsed ? "expand_less" : "expand_more" }}</md-icon>
      </a>
      <template v-if="!infoCollapsed">
        <md-switch v-model="filters.includeBWare" @change="debounceApplyFilters" class="md-primary">{{ $t("products.includeBWare") }}</md-switch>
        <div class="pd-dock-info-row">
          <span>{{ $t("products.count") }}</span>
          <strong>{{ totalItems }}</strong>
        </div>
      </template>
    </div>
    <CreateProductModal v-if="createModal" @close="hideCreateModal" @saved="onProductCreated" />
    <ImageZoom :url="zoomUrl" @close="zoomUrl = null" />

    <modal v-if="editModal" @close="hideEditModal" class-name="shipping-plan-modal-container product-edit-modal">
      <template slot="header">
        <h4 class="modal-title">Edit Product</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="hideEditModal">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <ValidationObserver v-slot="{}" ref="editForm">
          <md-tabs ref="editTabs" @md-changed="activeEditTab = $event">
            <md-tab id="ProductGeneral" :md-label="$t('products.ProductGeneral')">
              <div class="product-image-manage">
                <img v-if="editImageUrl" :src="editImageUrl" class="product-image-manage__preview" @click="zoomUrl = editImageUrl" />
                <div class="product-image-manage__actions">
                  <template v-if="editImageUrl">
                    <md-button class="md-simple md-sm" :title="$t('products.image.copyUrl')" @click="copyImageUrl"><md-icon>content_copy</md-icon>&nbsp;{{ $t("products.image.copyUrl") }}</md-button>
                    <md-button v-if="hasPermission('Write', 'Products', 'Image')" class="md-simple md-sm md-danger" :disabled="editImageBusy" @click="deleteEditImage">
                      <md-icon>delete</md-icon>&nbsp;{{ $t("products.image.delete") }}
                    </md-button>
                  </template>
                  <md-button v-if="hasPermission('Write', 'Products', 'Image')" class="md-simple md-sm" :disabled="editImageBusy" @click="$refs.imageFile.click()">
                    <md-icon>upload</md-icon>&nbsp;{{ editImageUrl ? $t("products.image.replace") : $t("products.image.upload") }}
                  </md-button>
                  <input ref="imageFile" type="file" accept=".jpg,.jpeg,.png" style="display: none" @change="uploadEditImage($event)" />
                </div>
              </div>
              <div class="md-layout md-gutter">
                <div class="md-layout-item md-size-40">
                  <ValidationProvider name="sku" rules="required" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.sku") }}</label>
                      <md-input v-model="sku" type="text" :disabled="!hasPermission('Write', 'Products', 'Sku')"> </md-input>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-30">
                  <ValidationProvider name="gtin" :rules="{ regex: /^[0-9]{8,14}$/ }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.gtin") }}</label>
                      <md-input v-model="gtin" type="text" inputmode="numeric" maxlength="14"></md-input>
                      <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-30">
                  <md-field>
                    <label>{{ $t("products.condition") }}</label>
                    <md-select v-model="condition" name="condition" id="condition">
                      <md-option value="Neu">{{ $t("products.conditionNew") }}</md-option>
                      <md-option value="B-Ware">{{ $t("products.conditionBWare") }}</md-option>
                    </md-select>
                  </md-field>
                </div>
              </div>
              <ValidationProvider name="name" rules="required" v-slot="{ passed, failed, errors }">
                <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                  <label>{{ $t("products.name") }}</label>
                  <md-input v-model="name" type="text" :disabled="!hasPermission('Write', 'Products', 'Name')"> </md-input>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </md-field>
              </ValidationProvider>
              <!-- Kompakt nebeneinander, damit der Reiter ohne Nachladen ganz passt (Trello 3b9GL2pS, Update B) -->
              <div class="md-layout md-gutter">
                <div class="md-layout-item md-size-25">
                  <md-field>
                    <label>{{ $t("products.shippingSizeLength") }}</label>
                    <md-input v-model="shippingSizeLength" type="number"> </md-input>
                    <span class="md-suffix">cm</span>
                  </md-field>
                </div>
                <div class="md-layout-item md-size-25">
                  <md-field>
                    <label>{{ $t("products.shippingSizeWidth") }}</label>
                    <md-input v-model="shippingSizeWidth" type="number"> </md-input>
                    <span class="md-suffix">cm</span>
                  </md-field>
                </div>
                <div class="md-layout-item md-size-25">
                  <md-field>
                    <label>{{ $t("products.shippingSizeHeight") }}</label>
                    <md-input v-model="shippingSizeHeight" type="number"> </md-input>
                    <span class="md-suffix">cm</span>
                  </md-field>
                </div>
                <div class="md-layout-item md-size-25">
                  <md-field>
                    <label>{{ $t("products.shippingSizeWeight") }}</label>
                    <md-input v-model="shippingSizeWeight" type="number"> </md-input>
                    <span class="md-suffix">kg</span>
                  </md-field>
                </div>
              </div>
              <div style="width: 30%">
                <md-switch v-model="bulky" value="true" class="md-primary">{{ $t("products.bulky") }}</md-switch>
              </div>
              <div class="md-layout md-gutter">
                <div class="md-layout-item md-size-25">
                  <ValidationProvider name="hsUK" rules="digits:8" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.hsUK") }}</label>
                      <md-input v-model="hsUK" type="number" :disabled="isBWare"> </md-input>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-25">
                  <ValidationProvider name="hsEU" rules="digits:10" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.hsEU") }}</label>
                      <md-input v-model="hsEU" type="number" :disabled="isBWare"> </md-input>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-25">
                  <ValidationProvider name="hsCH" rules="integer" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.hsCH") }}</label>
                      <md-input v-model="hsCH" type="number" :disabled="isBWare"> </md-input>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-25">
                  <ValidationProvider name="customsTariff" :rules="{ min_value: 0.0, decimal: 1 }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.customsTariff") }}</label>
                      <md-input v-model="customsTariff" type="number" min="0.0" step="0.1" inputmode="decimal" :disabled="isBWare"> </md-input>
                      <span class="md-suffix">%</span>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
              </div>
              <div class="md-layout md-gutter">
                <div class="md-layout-item md-size-33">
                  <ValidationProvider name="outboundCarrier" rules="required" v-slot="{ errors }">
                    <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 15px">
                      <label v-if="!!outboundCarrierName">{{ $t("products.outboundCarrier") }}</label>
                      <SelectCarrier v-model="outboundCarrierName" :allowedCarriers="allowedCarriers" :placeholder="$t('products.outboundCarrier')" :multiple="false" :customClass="'custom-select'" />
                      <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                    </div>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-33">
                  <ValidationProvider name="carrierUK" rules="required" v-slot="{ errors }">
                    <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 15px">
                      <label v-if="!!carrierUKName">{{ $t("products.carrierUK") }}</label>
                      <SelectCarrier v-model="carrierUKName" :allowedCarriers="allowedCarriers" :placeholder="$t('products.carrierUK')" :multiple="false" :customClass="'custom-select'" />
                      <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                    </div>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-33">
                  <ValidationProvider name="carrierEU" rules="required" v-slot="{ errors }">
                    <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 15px">
                      <label v-if="!!carrierEUName">{{ $t("products.carrierEU") }}</label>
                      <SelectCarrier v-model="carrierEUName" :allowedCarriers="allowedCarriers" :placeholder="$t('products.carrierEU')" :multiple="false" :customClass="'custom-select'" />
                      <div class="md-error" v-if="errors[0]">{{ $t(errors[0]) }}</div>
                    </div>
                  </ValidationProvider>
                </div>
              </div>
              <div class="md-layout md-gutter">
                <div class="md-layout-item md-size-50">
                  <ValidationProvider name="supplierId" rules="required" v-slot="{ errors }">
                    <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-top: 15px">
                      <label v-if="supplierIds.length">{{ $t("products.supplier") }}</label>
                      <SelectSupplier v-model="supplierIds" multiple :clearable="false" :placeholder="$t('products.supplier')" custom-class="custom-select" />
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                      <div v-if="!errors[0]" class="supplier-hint">{{ $t("products.supplierHint") }}</div>
                    </div>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-25">
                  <ValidationProvider name="moq" rules="integer|min_value:0" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.moq") }}</label>
                      <md-input v-model="moq" type="number" min="0" step="1"> </md-input>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-25" style="text-align: left">
                  <md-switch v-model="eol" class="md-primary">{{ $t("products.eol") }}</md-switch>
                </div>
              </div>
            </md-tab>
            <md-tab id="ProductDetails" :md-label="$t('products.ProductDetails')">
              <ValidationProvider name="cabQuantity" rules="integer|min_value:0" v-slot="{ passed, failed, errors }">
                <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                  <label>{{ $t("products.cabQuantity") }}</label>
                  <md-input v-model="cabQuantity" type="number"> </md-input>

                  <span class="md-suffix">pcs</span>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </md-field>
              </ValidationProvider>

              <md-subheader>{{ $t("products.masterboxDementions") }}</md-subheader>
              <div class="md-layout md-gutter mb-3">
                <div class="md-layout-item md-size-50 mb-3">
                  <ValidationProvider name="skuMasterbox" :rules="{ regex: /^[a-zA-Z0-9_-]+$/ }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.skuMasterbox") }}</label>
                      <md-input v-model="skuMasterbox" type="text"> </md-input>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-50 mb-3">
                  <ValidationProvider name="mbSkuAmount" rules="min_value:0|integer" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.mbSkuAmount") }}</label>
                      <md-input v-model="mbSkuAmount" type="number" min="0"> </md-input>
                      <span class="md-suffix">pcs</span>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-50 mb-3">
                  <ValidationProvider name="mbSkuLength" :rules="{ min_value: 0.0, decimal: 1 }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.mbSkuLength") }}</label>
                      <md-input v-model="mbSkuLength" type="number" min="0.1" step="0.1" inputmode="decimal"> </md-input>
                      <span class="md-suffix">cm</span>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-50 mb-3">
                  <ValidationProvider name="mbSkuWidth" :rules="{ min_value: 0.0, decimal: 1 }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.mbSkuWidth") }}</label>
                      <md-input v-model="mbSkuWidth" type="number" min="0.1" step="0.1" inputmode="decimal"> </md-input>
                      <span class="md-suffix">cm</span>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-50 mb-3">
                  <ValidationProvider name="mbSkuHeight" :rules="{ min_value: 0.0, decimal: 1 }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.mbSkuHeight") }}</label>
                      <md-input v-model="mbSkuHeight" type="number" min="0.1" step="0.1" inputmode="decimal"> </md-input>
                      <span class="md-suffix">cm</span>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-50 mb-3">
                  <ValidationProvider name="mbSkuWeight" :rules="{ min_value: 0.0, decimal: 2 }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.mbSkuWeight") }}</label>
                      <md-input v-model="mbSkuWeight" type="number" min="0.1" step="0.1" inputmode="decimal"> </md-input>
                      <span class="md-suffix">kg</span>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
              </div>
              <md-divider></md-divider>
              <md-subheader>{{ $t("products.shippingWeights") }}</md-subheader>
              <div class="md-layout md-gutter">
                <div class="md-layout-item md-size-50 mb-3">
                  <ValidationProvider name="skuPaperWeight" :rules="{ min_value: 0.0, decimal: 1 }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.skuPaperWeight") }}</label>
                      <md-input v-model="skuPaperWeight" type="number" min="0.1" step="0.1" inputmode="decimal"> </md-input>
                      <span class="md-suffix">gr</span>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-50 mb-3">
                  <ValidationProvider name="skuPlasticWeight" :rules="{ min_value: 0.0, decimal: 1 }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.skuPlasticWeight") }}</label>
                      <md-input v-model="skuPlasticWeight" type="number" min="0.1" step="0.1" inputmode="decimal"> </md-input>
                      <span class="md-suffix">gr</span>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-50 mb-3">
                  <ValidationProvider name="skuWoodWeight" :rules="{ min_value: 0.0, decimal: 1 }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.skuWoodWeight") }}</label>
                      <md-input v-model="skuWoodWeight" type="number" min="0.1" step="0.1" inputmode="decimal"> </md-input>
                      <span class="md-suffix">gr</span>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
                <div class="md-layout-item md-size-50 mb-3">
                  <ValidationProvider name="skuMetalWeight" :rules="{ min_value: 0.0, decimal: 1 }" v-slot="{ passed, failed, errors }">
                    <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                      <label>{{ $t("products.skuMetalWeight") }}</label>
                      <md-input v-model="skuMetalWeight" type="number" min="0.1" step="0.1" inputmode="decimal"> </md-input>
                      <span class="md-suffix">gr</span>
                      <div class="md-error" v-if="errors[0]">
                        {{ $t(errors[0]) }}
                      </div>
                    </md-field>
                  </ValidationProvider>
                </div>
              </div>
            </md-tab>
            <md-tab id="ProductCalculation" :md-label="$t('products.ProductCalculation')">
              <ProductCalculation v-if="activeEditTab === 'ProductCalculation'" :key="id" :product-id="id" />
            </md-tab>
            <md-tab id="ProductPriceHistory" :md-label="$t('products.ProductPriceHistory')">
              <PriceHistory v-if="activeEditTab === 'ProductPriceHistory'" :key="id" :sku="sku" />
            </md-tab>
          </md-tabs>
        </ValidationObserver>
      </template>

      <template slot="footer">
        <md-button class="md-secondary mr-1" @click="hideEditModal">{{ $t("products.close") }}</md-button>
        <md-button class="md-success" @click="editProduct">{{ $t("products.save") }}</md-button>
      </template>
    </modal>

    <md-dialog :md-active.sync="showConfirmDialog" :md-click-outside-to-close="false">
      <md-dialog-title> {{ $t("products.messages.confirmCreateBGood") }} </md-dialog-title>
      <md-dialog-content>{{ $t("products.messages.confirmCreateBGoodMessage") }} </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-secondary" @click="hideConfirmBGoodModal">{{ $t("products.cancel") }}</md-button>
        <md-button class="md-success" @click="handleCreateBGood">{{ $t("products.confirm") }}</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>
<script>
import { debounce } from "lodash";
import { Modal } from "@/components";
import axiosFactory from "../../services/axios.factory";
import CSLoading from "../../components/Loading/CSLoading.vue";
import { EventBus } from "@/helpers/event-bus";
import { hasPermission } from "../../mixins/auth";
import CreateProductModal from "./CreateProductModal.vue";
import ProductCalculation from "./ProductCalculation.vue";
import PriceHistory from "@/components/PriceHistory.vue";
import ProductImage, { clearProductImageCache } from "@/components/Products/ProductImage.vue";
import ImageZoom from "@/components/Products/ImageZoom.vue";
import SelectCarrier from "../../components/SelectCarrier/SelectCarrier.vue";
import SelectSupplier from "../../components/SelectSupplier/SelectSupplier.vue";
import InventoryDetailTooltip from "@/components/Wms/InventoryDetailTooltip.vue";
import HoverPopover from "@/components/HoverPopover.vue";
import ProductFilterDrawer from "./ProductFilterDrawer.vue";
import SortMenu from "./SortMenu.vue";

const CARRIER_LOGOS = { DHL: "/img/logo-dhl.png", DPD: "/img/logo-dpd.png", Spedition: "/img/logo-speedition.png" };

export default {
  components: {
    Modal,
    CSLoading,
    CreateProductModal,
    ProductCalculation,
    PriceHistory,
    ProductImage,
    ImageZoom,
    SelectCarrier,
    SelectSupplier,
    InventoryDetailTooltip,
    HoverPopover,
    ProductFilterDrawer,
    SortMenu,
  },
  data() {
    return {
      isLoading: true,
      createModal: false,
      editModal: false,
      zoomUrl: null,
      editImageUrl: null,
      editImageBusy: false,
      sku: null,
      gtin: null,
      name: null,
      inStock: null,
      inFlow: null,
      shippingSizeLength: null,
      shippingSizeWidth: null,
      shippingSizeHeight: null,
      shippingSizeWeight: null,
      condition: null,
      hsUK: null,
      hsEU: null,
      hsCH: null,
      customsTariff: null,
      outboundCarrier: null,
      carrierUK: null,
      carrierEU: null,
      bulky: "",
      supplierIds: [],
      moq: null,
      eol: false,
      id: null,
      cabQuantity: null,
      mbSkuAmount: null,
      mbSkuHeight: null,
      mbSkuLength: null,
      mbSkuWeight: null,
      mbSkuWidth: null,
      skuMasterbox: null,
      skuMetalWeight: null,
      skuPaperWeight: null,
      skuPlasticWeight: null,
      skuWoodWeight: null,
      dockSearchOpen: false,
      infoCollapsed: false,
      currentOrder: { column: "id", ascending: false },
      stockDetails: {},
      stockLoading: {},
      inflows: {},
      filters: {
        keywords: "",
        sku: "",
        exactMatchSku: false,
        bulky: "all",
        includeBWare: false,
        supplierId: null,
      },
      drawerFilters: {},
      tableColumns: ["actions", "image", "id", "stock", "shippingStats", "shippingSize", "hs", "outboundCarrier", "kpi", "supplier", "masterboxDementions", "shippingWeights"],
      tableOptions: {
        headings: {
          actions: () => this.$t("products.table.actions"),
          image: () => "",
          stock: () => this.$t("products.table.stock"),
          shippingSize: () => this.$t("products.table.shippingSizeBulky"),
          outboundCarrier: () => this.$t("products.table.carriers"),
          supplier: () => this.$t("products.table.supplierBlock"),
          masterboxDementions: () => this.$t("products.masterboxDementions"),
          shippingWeights: () => this.$t("products.shippingWeights"),
        },
        perPage: 25,
        perPageValues: [25, 100, 5000],
        filterable: false,
        sortIcon: {
          is: "fa-sort",
          base: "fa",
          up: "fa-sort-up",
          down: "fa-sort-down",
        },
        sortable: [],
        params: {},
        orderBy: { column: "id", ascending: false },
        resizableColumns: false,
        requestFunction: this.requestFunction,
        responseAdapter: this.responseFunction,
        rowClassCallback: this.rowClassCallback,
        pagination: {
          show: true,
        },
        texts: {
          count: this.$t("table.count"),
          limit: this.$t("table.records"),
          noResults: this.$t("table.noRecord"),
        },
        columnsClasses: {
          id: "text-left col-identity",
          stock: "text-left",
          masterboxDementions: "text-left",
          shippingWeights: "text-left",
          shippingStats: "text-left shipping-stats-header",
          shippingSize: "text-left",
          outboundCarrier: "text-left",
          kpi: "text-left col-kpi",
          supplier: "text-left",
        },
      },
      tableData: [],
      totalItems: 0,
      table: null,
      showConfirmDialog: false,
      selectedProduct: null,
      activeEditTab: null,
    };
  },
  computed: {
    identitySortOptions() {
      return [
        { key: "id", label: "ID" },
        { key: "sku", label: this.$t("products.sku") },
        { key: "name", label: this.$t("products.name") },
      ];
    },
    shippingStatsSortOptions() {
      return [
        { key: "productsShippingLastDay", label: "1d" },
        { key: "productsShipping7Days", label: "7d" },
        { key: "productsShipping30Days", label: "30d" },
        { key: "productsShipping90Days", label: "90d" },
        { key: "productsShipping365Days", label: "365d" },
      ];
    },
    kpiSortOptions() {
      const keys = [
        ["sold12", "sold12"],
        ["sold3", "sold3"],
        ["coverage12", "coverage12"],
        ["coverage3", "coverage3"],
        ["returnRate", "returnRate"],
        ["returns3", "returns3"],
        ["storagePerMonth", "storagePerPiece"],
        ["averageStock", "averageStock"],
        ["storageMonths", "storageMonths"],
        ["storageCost", "storageCost"],
        ["turnover", "turnover"],
      ];
      return keys.map(([key, label]) => ({ key: "kpi_" + key, label: this.$t("productCalculation.figure." + label) }));
    },
    isBWare() {
      return this.condition === "B-Ware";
    },
    supplierNameOptions() {
      return this.$store.state.shippingData.supplierNames.map((s) => ({ value: s.id, name: s.name })) || [];
    },
    carrierOptions() {
      return (this.$store.state.shippingData.carriers || []).filter((c) => this.allowedCarriers.includes(c.name));
    },
    allowedCarriers() {
      return ["DHL", "DPD", "Spedition"];
    },
    outboundCarrierName: {
      get() {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.id === this.outboundCarrier);
        return c ? c.name : null;
      },
      set(name) {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.name === name);
        this.outboundCarrier = c ? c.id : null;
      },
    },
    carrierUKName: {
      get() {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.id === this.carrierUK);
        return c ? c.name : null;
      },
      set(name) {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.name === name);
        this.carrierUK = c ? c.id : null;
      },
    },
    carrierEUName: {
      get() {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.id === this.carrierEU);
        return c ? c.name : null;
      },
      set(name) {
        const c = (this.$store.state.shippingData.carriers || []).find((x) => x.name === name);
        this.carrierEU = c ? c.id : null;
      },
    },
  },
  methods: {
    hideCreateModal() {
      this.createModal = false;
    },
    onProductCreated() {
      this.applyFilters({});
      this.hideCreateModal();
    },
    // md-tabs misst die Hoehe nur bei DOM-Mutationen; waechst der Reiter spaeter (Auswahllisten, Bild), blieb das Ende mit MOQ/EOL
    // abgeschnitten, bis ein Klick neu mass (Trello 3b9GL2pS, Update B). Der ResizeObserver fuehrt die Hoehe nach.
    observeEditTabs() {
      this.$nextTick(() => {
        const tabs = this.$refs.editTabs;
        if (!tabs || typeof ResizeObserver === "undefined") return;
        this.editTabsObserver = new ResizeObserver(() => tabs.calculateTabPos());
        tabs.$el.querySelectorAll(".md-tab").forEach((el) => this.editTabsObserver.observe(el));
      });
    },
    hideEditModal() {
      if (this.editTabsObserver) this.editTabsObserver.disconnect();
      this.editTabsObserver = null;
      this.editModal = false;
      this.activeEditTab = null;
      this.resetForm();
    },
    resetForm() {
      this.condition = null;
      this.gtin = null;
      this.hsUK = null;
      this.hsEU = null;
      this.hsCH = null;
      this.customsTariff = null;
      this.supplierIds = [];
      this.moq = null;
      this.eol = false;

      this.cabQuantity = null;
      this.mbSkuAmount = null;
      this.mbSkuHeight = null;
      this.mbSkuLength = null;
      this.mbSkuWeight = null;
      this.mbSkuWidth = null;
      this.skuMasterbox = null;
      this.skuMetalWeight = null;
      this.skuPaperWeight = null;
      this.skuPlasticWeight = null;
      this.skuWoodWeight = null;

      // this.sku = null;
      // this.name = null;
      // this.shippingSizeLength = null;
      // this.shippingSizeWidth = null;
      // this.shippingSizeHeight = null;
      // this.shippingSizeWeight = null;
      // this.outboundCarrier = null;
      // this.carrierUK = null;
      // this.carrierEU = null;
      // this.bulky = "";
    },
    showCreateModal(product = {}) {
      this.sku = product.sku;
      this.name = product.name;
      this.shippingSizeLength = product.shippingSizeLength;
      this.shippingSizeWidth = product.shippingSizeWidth;
      this.shippingSizeHeight = product.shippingSizeHeight;
      this.shippingSizeWeight = product.shippingSizeWeight;
      this.outboundCarrier = product.outboundCarrier;
      this.carrierUK = product.carrierUK;
      this.carrierEU = product.carrierEU;
      this.bulky = "";
      this.createModal = true;
    },
    showEditModal(product) {
      this.sku = product.sku;
      this.gtin = product.gtin;
      this.name = product.name;
      this.inStock = product.inStock;
      this.inFlow = product.inFlow;
      this.shippingSizeLength = product.shippingSizeLength;
      this.shippingSizeWidth = product.shippingSizeWidth;
      this.shippingSizeHeight = product.shippingSizeHeight;
      this.shippingSizeWeight = product.shippingSizeWeight;
      this.condition = product.condition;
      this.hsUK = product.hsUK;
      this.hsEU = product.hsEU;
      this.hsCH = product.hsCH;
      this.customsTariff = product.customsTariff;

      this.cabQuantity = product.cabQuantity;
      this.mbSkuAmount = product.mbSkuAmount;
      this.mbSkuHeight = product.mbSkuHeight;
      this.mbSkuLength = product.mbSkuLength;
      this.mbSkuWeight = product.mbSkuWeight;
      this.mbSkuWidth = product.mbSkuWidth;
      this.skuMasterbox = product.skuMasterbox;
      this.skuMetalWeight = product.skuMetalWeight;
      this.skuPaperWeight = product.skuPaperWeight;
      this.skuPlasticWeight = product.skuPlasticWeight;
      this.skuWoodWeight = product.skuWoodWeight;

      let carrier = this.carrierOptions.find((i) => i.name == product.outboundCarrier);
      this.outboundCarrier = carrier && carrier.id;
      carrier = this.carrierOptions.find((i) => i.name == product.carrierUK);
      this.carrierUK = carrier && carrier.id;
      carrier = this.carrierOptions.find((i) => i.name == product.carrierEU);
      this.carrierEU = carrier && carrier.id;
      this.supplierIds = (product.suppliers || []).map((s) => s.id);
      this.moq = product.moq;
      this.eol = !!product.eol;
      this.bulky = product.bulky;
      this.id = product.id;
      this.activeEditTab = null;
      this.editModal = true;
      this.observeEditTabs();
      this.loadEditImage();
    },
    loadEditImage() {
      if (this.editImageUrl) URL.revokeObjectURL(this.editImageUrl);
      this.editImageUrl = null;
      axiosFactory()
        .get(`/shipping_data/products/${this.id}/image/content`, { responseType: "blob" })
        .then((res) => {
          this.editImageUrl = URL.createObjectURL(new Blob([res.data]));
        })
        .catch(() => {});
    },
    uploadEditImage(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      this.editImageBusy = true;
      const formData = new FormData();
      formData.append("file", file);
      axiosFactory()
        .post(`/shipping_data/products/${this.id}/image`, formData, { headers: { "Content-Type": "multipart/form-data" } })
        .then(() => {
          clearProductImageCache(this.sku);
          this.loadEditImage();
        })
        .catch(() => {
          this.$notify({ timeout: 3000, message: this.$t("products.image.uploadFailed"), icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type: "danger" });
        })
        .finally(() => {
          this.editImageBusy = false;
        });
    },
    deleteEditImage() {
      this.editImageBusy = true;
      axiosFactory()
        .delete(`/shipping_data/products/${this.id}/image`)
        .then(() => {
          clearProductImageCache(this.sku);
          this.editImageUrl = null;
        })
        .finally(() => {
          this.editImageBusy = false;
        });
    },
    copyImageUrl() {
      const url = `${process.env.VUE_APP_API_BASE_URL}/api/shipping_data/products/${this.id}/image/content`;
      navigator.clipboard.writeText(url);
      this.$notify({ timeout: 2000, message: this.$t("products.image.copied"), icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type: "success" });
    },

    hideConfirmBGoodModal() {
      this.showConfirmDialog = false;
      this.selectedProduct = null;
    },
    async handleCreateBGood() {
      this.showConfirmDialog = false;
      // create product b good
      this.isLoading = true;
      const supplier = this.supplierNameOptions.find((i) => i.name.includes("B-WARE"));
      const payload = {
        ...this.selectedProduct,
        sku: this.selectedProduct.sku + "_bware",
        name: "B-Ware - " + this.selectedProduct.name,
        supplierId: supplier?.value,
        supplierIds: supplier ? [supplier.value] : [],
        condition: "B-Ware",
        hsUK: null,
        hsEU: null,
        hsCH: null,
        customsTariff: null,
        skuMasterbox: null,
        mbSkuAmount: null,
        mbSkuLength: null,
        mbSkuWidth: null,
        mbSkuHeight: null,
        mbSkuWeight: null,
        skuPaperWeight: null,
        skuPlasticWeight: null,
        skuWoodWeight: null,
        skuMetalWeight: null,
        gtin: null,
      };
      delete payload.id;
      delete payload.supplier;
      delete payload.suppliers;
      console.log("create product b-good", payload);
      this.$store
        .dispatch("shippingData/createProduct", payload)
        .then(() => {
          this.$notify({
            timeout: 2500,
            message: this.$t("products.messages.createdBGoodProduct", { productName: payload.name }),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });

          this.applyFilters({});
        })
        .catch(() => {
          this.$notify({
            timeout: 2500,
            message: this.$t("products.messages.canNotCreateBGoodProduct"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "error",
          });
        })
        .finally(() => {
          this.isLoading = false;
          this.selectedProduct = null;
        });
    },

    /**
     * 
     * A.) Check whether a B-goods item has been selected. If the SKU you select ends with "_bware", a message should appear: "No B-goods product can be created for B-goods products." (IF "SKU" ends with "_bware" THEN TXTBOX ELSE next.

B.) Check whether the product already exists. Example: For product "BBQ11" I would like to create a "B-goods product", then it should be checked whether "BBQ11_bware" already exists. If yes, then the message: "The SKU BBQ11_bware already exists. Nothing has been created. Otherwise continue.

C.) Lightbox with renewed query: "Do you really want to create a "B-goods product" for this product?" with 2 buttons: "Cancel" and "Confirm". If you click on "Cancel", the lightbox should be closed and nothing should happen. Click on "Confirm" to continue.

D.) Now a new product is to be created with the following content:

SKU: As selected + "_bware" (So SOMO3A was selected, the new product then gets SOMO3A_bware as SKU)

Name: As selected + BEFORE "B-Ware - " (Take a look at the current ones, you can see it there)

Parcel length, width, height, weight, bulky goods, all HS codes: As selected

Supplier: CHANGE to "B-goods (placeholder)"

*Are there any other fields to check?

E.) Message: The product "NAMING THE NEW SKU HERE" has been created.
     * @param product 
     */
    async handleCreateBulkGood(product) {
      // this.$router.push({ name: "BulkGoods", params: { sku: product.sku } });
      // cas A
      if (product.sku.endsWith("_bware")) {
        this.$notify({
          timeout: 2500,
          message: this.$t("products.messages.bwareProductCanNotCreateBGood"),
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "error",
        });
        return;
      }
      this.isLoading = true;
      // case B
      let sku = product.sku + "_bware";
      let productExist = await this.findProduct(sku);
      console.log("productExist", productExist);
      if (productExist) {
        this.$notify({
          timeout: 2500,
          message: this.$t("products.messages.productExistBGood", { sku }),
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "error",
        });
        this.isLoading = false;
        return;
      }
      // case C
      else {
        this.showConfirmDialog = true;
        this.selectedProduct = product;
      }
    },
    async findProduct(sku) {
      // try find in current list
      let found = this.tableData.find((i) => i.sku === sku);
      if (found) {
        return found;
      }

      found = await axiosFactory()
        .get(`/shipping_data/products`, { params: { keywords: sku, bulky: "all", pageIndex: 0, pageSize: 25, includeBWare: true } })
        .catch((e) => {
          console.error("Produktliste laden fehlgeschlagen", e);
          return { data: { data: [], total: 0 } };
        })
        .then((res) => {
          return res.data.data.length > 0 ? res.data.data[0] : null;
        })
        .finally(() => {
          this.isLoading = false;
        });

      return found;
    },
    createProduct() {
      this.$refs.createForm.validate().then((success) => {
        if (!success) {
          this.$notify({
            timeout: 2500,
            message: this.$t("products.messages.checkFields"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "danger",
          });
          return;
        }

        this.isLoading = true;
        let carrier = this.carrierOptions.find((i) => i.id === this.outboundCarrier);
        let carrierUK = this.carrierOptions.find((i) => i.id === this.carrierUK);
        let carrierEU = this.carrierOptions.find((i) => i.id === this.carrierEU);
        const payload = {
          sku: this.sku,
          gtin: this.gtin || null,
          name: this.name,
          shippingSizeLength: this.shippingSizeLength ? parseFloat(this.shippingSizeLength) : 0,
          shippingSizeWidth: this.shippingSizeWidth ? parseFloat(this.shippingSizeWidth) : 0,
          shippingSizeHeight: this.shippingSizeHeight ? parseFloat(this.shippingSizeHeight) : 0,
          shippingSizeWeight: this.shippingSizeWeight ? parseFloat(this.shippingSizeWeight) : 0,
          condition: this.condition || null,
          hsUK: this.hsUK,
          hsEU: this.hsEU,
          hsCH: this.hsCH,
          customsTariff: this.customsTariff === "" || this.customsTariff === null || this.customsTariff === undefined ? null : parseFloat(this.customsTariff),
          outboundCarrier: carrier && carrier.name,
          carrierUK: carrierUK && carrierUK.name,
          carrierEU: carrierEU && carrierEU.name,
          bulky: this.bulky ? this.bulky : "false",
          supplierIds: this.supplierIds,
          cabQuantity: this.cabQuantity,
          mbSkuAmount: this.mbSkuAmount,
          mbSkuHeight: this.mbSkuHeight,
          mbSkuLength: this.mbSkuLength,
          mbSkuWeight: this.mbSkuWeight,
          mbSkuWidth: this.mbSkuWidth,
          skuMasterbox: this.skuMasterbox,
          skuMetalWeight: this.skuMetalWeight,
          skuPaperWeight: this.skuPaperWeight,
          skuPlasticWeight: this.skuPlasticWeight,
          skuWoodWeight: this.skuWoodWeight,
        };
        // console.log("create product", payload);
        this.$store
          .dispatch("shippingData/createProduct", payload)
          .then(() => {
            this.$notify({
              timeout: 2500,
              message: this.$t("products.messages.created"),
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "success",
            });

            this.applyFilters({});
          })
          .then(() => {
            this.hideCreateModal();
          })
          .catch(() => {
            this.$notify({
              timeout: 2500,
              message: this.$t("products.messages.productSkuDuplicated"),
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "error",
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
      });
    },
    editProduct() {
      this.$refs.editForm.validate().then((success) => {
        if (!success) {
          this.$notify({
            timeout: 2500,
            message: this.$t("products.messages.checkFields"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "danger",
          });
          return;
        }

        this.isLoading = true;
        let carrier = this.carrierOptions.find((i) => i.id === this.outboundCarrier);
        let carrierUK = this.carrierOptions.find((i) => i.id === this.carrierUK);
        let carrierEU = this.carrierOptions.find((i) => i.id === this.carrierEU);
        const payload = {
          sku: this.sku,
          gtin: this.gtin || null,
          name: this.name,
          shippingSizeLength: this.shippingSizeLength ? parseFloat(this.shippingSizeLength) : 0,
          shippingSizeWidth: this.shippingSizeWidth ? parseFloat(this.shippingSizeWidth) : 0,
          shippingSizeHeight: this.shippingSizeHeight ? parseFloat(this.shippingSizeHeight) : 0,
          shippingSizeWeight: this.shippingSizeWeight ? parseFloat(this.shippingSizeWeight) : 0,
          condition: this.condition || null,
          hsUK: this.hsUK,
          hsEU: this.hsEU,
          hsCH: this.hsCH,
          customsTariff: this.customsTariff === "" || this.customsTariff === null || this.customsTariff === undefined ? null : parseFloat(this.customsTariff),
          outboundCarrier: carrier && carrier.name,
          carrierUK: carrierUK && carrierUK.name,
          carrierEU: carrierEU && carrierEU.name,
          bulky: this.bulky ? this.bulky : "false",
          supplierIds: this.supplierIds,
          moq: this.moq === "" || this.moq === null || this.moq === undefined ? null : parseInt(this.moq, 10),
          eol: !!this.eol,
          cabQuantity: this.cabQuantity,
          mbSkuAmount: this.mbSkuAmount,
          mbSkuHeight: this.mbSkuHeight,
          mbSkuLength: this.mbSkuLength,
          mbSkuWeight: this.mbSkuWeight,
          mbSkuWidth: this.mbSkuWidth,
          skuMasterbox: this.skuMasterbox,
          skuMetalWeight: this.skuMetalWeight,
          skuPaperWeight: this.skuPaperWeight,
          skuPlasticWeight: this.skuPlasticWeight,
          skuWoodWeight: this.skuWoodWeight,
        };
        // console.log("edit product", payload);
        axiosFactory()
          .put("shipping_data/products/" + this.id, payload)
          .then(() => {
            this.$notify({
              timeout: 2500,
              message: this.$t("products.messages.updated"),
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "success",
            });

            this.applyFilters({});
          })
          .then(() => {
            this.hideEditModal();
          })
          .finally(() => {
            this.isLoading = false;
          });
      });
    },
    requestFunction(data) {
      const { page, limit, orderBy, ascending, pageIndex, ...filters } = data;
      this.currentPage = page;
      this.currentOrder = { column: orderBy, ascending: !!ascending };
      let params = {
        pageIndex: this.currentPage,
        pageSize: limit,
        orderBy,
        ascending,
        ...filters,
      };
      if (this.filters.sku) {
        params.sku = this.filters.sku;
        params.exactMatchSku = this.filters.exactMatchSku;
      }
      this.isLoading = true;
      return axiosFactory()
        .get(`/shipping_data/products`, { params })
        .catch((e) => {
          console.error("Produktliste laden fehlgeschlagen", e);
          return { data: { data: [], total: 0 } };
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    responseFunction({ data }) {
      this.tableData = data.data.map((i) => ({ ...i, selected: false }));
      this.totalItems = data.total;

      return {
        data: this.tableData,
        count: data.total,
      };
    },
    rowClassCallback(row) {
      return "";
    },
    debounceApplyFilters: debounce(function () {
      this.applyFilters({});
    }, 300),
    applyFilters(filters) {
      this.$refs.table.options.params = {
        ...this.$refs.table.options.params,
        ...this.filters,
        ...this.drawerFilters,
        ...filters,
        pageIndex: 0,
      };
      this.currentPage = 1;
      this.$refs.table.setPage(this.currentPage);
    },
    hasPermission(permissionName, tableName, columnName) {
      return hasPermission(permissionName, tableName, columnName);
    },
    sortBy(column, ascending) {
      this.$refs.table.setOrder(column, ascending);
    },
    onDrawerFilters(drawerFilters) {
      // Lieferant und Sperrgut kommen jetzt aus der Seitenleiste
      this.filters.supplierId = drawerFilters.supplierId;
      this.filters.bulky = drawerFilters.bulky;
      this.drawerFilters = drawerFilters;
      this.applyFilters({});
    },
    async loadStock(sku) {
      if (!sku || this.stockLoading[sku] || this.stockDetails[sku]) return;
      this.$set(this.stockLoading, sku, true);
      try {
        const rows = await this.$store.dispatch("warehouses/getWarehouseInventoriesBySku", { sku });
        this.$set(this.stockDetails, sku, Array.isArray(rows) ? rows : []);
      } catch (e) {
        this.$set(this.stockDetails, sku, []);
      } finally {
        this.$set(this.stockLoading, sku, false);
      }
    },
    stockRows(row) {
      return (this.stockDetails[row.sku] || []).map((i, index) => ({
        key: `${i.id || i.storageLocationId || index}`,
        storageLocation: i.inventoryName,
        quantity: i.inventory ?? 0,
        goodsReceiptDate: i.date || null,
      }));
    },
    loadInflow(row) {
      if (!row.inFlow || this.inflows[row.id]) return;
      axiosFactory()
        .get(`/shipping_data/products/${row.id}/inflow`)
        .then((res) => this.$set(this.inflows, row.id, (res.data && res.data.data) || []))
        .catch(() => this.$set(this.inflows, row.id, []));
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString(this.$i18n.locale);
    },
    dimensions(row) {
      const n = (v) => (v == null ? "–" : this.$options.filters.formatNumberGerman(v, v % 1 === 0 ? 0 : 1));
      return `${n(row.shippingSizeLength)} x ${n(row.shippingSizeWidth)} x ${n(row.shippingSizeHeight)} cm`;
    },
    carrierLines(row) {
      return [
        { region: "DE", name: row.outboundCarrier, cost: row.kpi ? row.kpi.shippingCostDe : null },
        { region: "UK", name: row.carrierUK, cost: null },
        { region: "EU", name: row.carrierEU, cost: null },
      ];
    },
    carrierLogo(name) {
      return CARRIER_LOGOS[name] || null;
    },
    // Kennzahlen paarweise je Zeile wie auf der Trello-Karte; Langtext im Mouseover
    kpiLines(k) {
      const num = (v, d) => (v == null ? "–" : this.$options.filters.formatNumberGerman(v, d));
      const eur = (v) => (v == null ? "–" : `${num(v, 2)} €`);
      const t = (key) => this.$t("productCalculation.figure." + key);
      const short = (key) => this.$t("products.kpi." + key);
      // Einheiten und Rundung wie in der Kalkulation (Trello 3b9GL2pS, Update N): Stueck ganzzahlig, Monate mit einer Nachkommastelle
      const pcs = (v) => (v == null ? "–" : `${num(v, 0)} ${this.$t("oqsSettings.units.pieces")}`);
      const months = (v) => (v == null ? "–" : `${num(v, 1)} ${this.$t("oqsSettings.units.months")}`);
      return [
        { key: "sold", label: short("sold"), title: `${t("sold12")} / ${t("sold3")}`, value: `${pcs(k.sold12Months)} / ${pcs(k.sold3Months)}` },
        { key: "coverage", label: short("coverage"), title: `${t("coverage12")} / ${t("coverage3")}`, value: `${months(k.coverage12Months)} / ${months(k.coverage3Months)}` },
        { key: "returns", label: short("returns"), title: `${t("returnRate")} (${t("returns3")})`, value: `${k.returnRatePercent == null ? "–" : num(k.returnRatePercent, 1) + " %"} (${pcs(k.returns3Months)})` },
        { key: "storage", label: short("storage"), title: `${t("storagePerPiece")} / ${t("averageStock")}`, value: `${eur(k.storageCostPerPieceMonth)} / ${pcs(k.averageStockPieces)}` },
        { key: "duration", label: short("duration"), title: `${t("storageMonths")} / ${t("storageCost")}`, value: `${months(k.averageStorageMonths)} / ${eur(k.averageStorageCostPerPiece)}` },
        { key: "turnover", label: short("turnover"), title: t("turnover"), value: num(k.stockTurnover, 2) },
      ];
    },
    exportProducts() {
      this.isLoading = true;
      axiosFactory()
        .post(`/shipping_data/export_products`)
        .then(() => {
          this.$notify({
            timeout: 2500,
            message: this.$t("products.exportSuccess"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
        })
        .catch(() => {
          this.$notify({
            timeout: 2500,
            message: this.$t("products.exportError"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "error",
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  updated() {
    EventBus.$emit("scroll:changed");
  },
  created() {
    this.$store.dispatch("shippingData/getSupplierNames");
    this.$store.dispatch("shippingData/getCarriers");
  },
};
</script>
<style scoped>
.cell-idline {
  color: #777;
  font-size: 12px;
}
.export-button {
  float: right;
  margin-top: 10px !important;
}
/* ID, SKU, GTIN, Name untereinander; Name bricht nach ca. 40 Zeichen um */
.cell-identity {
  text-align: left;
  max-width: 40ch;
  white-space: normal;
}
.cell-sku {
  font-weight: 700;
  font-size: 14px;
}
.cell-identity .cell-name {
  line-height: 1.3;
  margin-top: 2px;
}
.cell-stock {
  text-align: left;
}
.inflow-value--active {
  cursor: default;
  border-bottom: 1px dotted #999;
}
.bulky-mark {
  color: #e65100;
  font-weight: 500;
}
.bulky-mark .material-symbols-outlined {
  font-size: 15px;
  vertical-align: text-bottom;
}
.cell-carrier .carrier-region {
  display: inline-block;
  min-width: 24px;
}
.carrier-icon {
  height: 16px;
  border-radius: 8px;
  vertical-align: middle;
}
.carrier-cost {
  margin-left: 6px;
  color: #555;
  font-size: 12px;
}
.cell-kpi {
  min-width: 210px;
  font-size: 12px;
}
.kpi-line {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  line-height: 19px;
  white-space: nowrap;
}
.kpi-label {
  color: #777;
  cursor: help;
}
.kpi-value {
  font-variant-numeric: tabular-nums;
}
.eol-indicator .md-icon {
  font-size: 18px !important;
  vertical-align: middle;
}
.eol-yes {
  color: #f44336 !important;
}
.eol-no {
  color: #7f915a !important;
}

/* Feste Leiste rechts wie im Lieferplan: Neuanlage, Suche, Filter */
.pd-dock {
  position: fixed;
  top: 110px;
  right: 0;
  z-index: 1031;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.pd-dock-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid #bbb;
  border-right: 0 none;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
}
.pd-dock-trigger .md-icon {
  color: #fff !important;
}
.pd-dock-trigger--active {
  background: #7f915a;
}
.pd-dock-search {
  position: relative;
}
.pd-dock-search-panel {
  position: absolute;
  right: 44px;
  top: 0;
  width: 320px;
  background: #fff;
  padding: 16px 16px 8px;
  border-radius: 6px 0 0 6px;
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.25);
  text-align: left;
}
.pd-dock-search-panel .md-field {
  margin: 0 0 12px 0;
}
/* Infobox unten rechts, einklappbar */
.pd-dock-info {
  position: fixed;
  bottom: 12px;
  right: 0;
  z-index: 1029;
  background: #fff;
  border: 1px solid #ddd;
  border-right: 0 none;
  border-radius: 8px 0 0 8px;
  padding: 4px 12px 6px;
  font-size: 12px;
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 200px;
}
.pd-dock-info-toggle {
  position: absolute;
  top: 2px;
  right: 4px;
  cursor: pointer;
  line-height: 1;
  z-index: 3;
  padding: 2px;
}
.pd-dock-info-toggle .md-icon {
  font-size: 18px !important;
  color: #999;
}
.pd-dock-info.pd-dock-info--collapsed {
  min-width: 0;
  width: 44px;
  height: 44px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pd-dock-info--collapsed .pd-dock-info-toggle {
  position: static;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.pd-dock-info .md-switch {
  margin: 4px 0;
}
.pd-dock-info-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.table-transparent {
  background-color: transparent !important;
}

.filter-supplier {
  padding-top: 14px;
}

.cell-supplier .supplier-primary {
  font-weight: 600;
}

.mt-0 {
  margin-top: 0 !important;
}

.mr-1 {
  margin-right: 15px;
}

.md-card .md-card-header.md-card-header-icon .material-symbols-outlined {
  width: 33px;
  height: 33px;
  text-align: center;
  line-height: 33px;
  color: #fff;
  margin: 0 !important;
  font-size: 28px !important;
}
.switch-toggle-label {
  font-weight: normal !important;
}
.md-subheader {
  padding-left: 0;
}
.product-image-manage {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
}
.product-image-manage__preview {
  max-width: 140px;
  max-height: 140px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  cursor: zoom-in;
}
</style>
<style>
/* Inhalt des Produkt-Dialogs scrollt im Dialog, statt ueber den Bildschirmrand hinauszuragen */
.product-edit-modal .modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
th.shipping-stats-header {
  white-space: pre-line !important;
}
/* Zulauf-Mouseover haengt am body, daher ungescoped */
.inflow-tooltip.md-tooltip {
  height: auto;
  max-width: none;
  padding: 6px 8px;
}
.inflow-table {
  border-collapse: collapse;
  font-size: 12px;
}
.inflow-table th,
.inflow-table td {
  padding: 1px 8px;
  text-align: left;
  white-space: nowrap;
}
.inflow-table td.text-right {
  text-align: right;
}
</style>
